import { useMemo } from 'react';
import { BoxGeometry, MeshStandardMaterial } from 'three';
import { SCALE_IN_TO_UNIT } from '../types';

interface Props {
  /** Z position of head platform center (scene units) */
  platformZ: number;
  /** Ramp width in inches */
  widthIn: number;
  /** Head platform depth in inches (to compute back edge) */
  platformDepthIn: number;
}

const WALL_HEIGHT_IN = 108; // 9 ft tall — typical building façade
const WALL_THICKNESS_IN = 6;
const WALL_WIDTH_EXTRA_IN = 24; // extend 1 ft beyond each side of ramp width

// Door dimensions: 36" wide × 80" tall, centered at x=0
const DOOR_WIDTH_IN = 36;
const DOOR_HEIGHT_IN = 80;

export function BuildingWall({ platformZ, widthIn, platformDepthIn }: Props) {
  const wallWidthIn = widthIn + WALL_WIDTH_EXTRA_IN;

  // Left wall segment: from x=-wallWidth/2 to door left edge (x=-doorWidth/2)
  const leftSegWidthIn = wallWidthIn / 2 - DOOR_WIDTH_IN / 2;
  const leftSegGeom = useMemo(
    () =>
      new BoxGeometry(
        leftSegWidthIn * SCALE_IN_TO_UNIT,
        WALL_HEIGHT_IN * SCALE_IN_TO_UNIT,
        WALL_THICKNESS_IN * SCALE_IN_TO_UNIT,
      ),
    [leftSegWidthIn],
  );

  // Right wall segment: from door right edge (x=+doorWidth/2) to x=+wallWidth/2
  const rightSegWidthIn = wallWidthIn / 2 - DOOR_WIDTH_IN / 2;
  const rightSegGeom = useMemo(
    () =>
      new BoxGeometry(
        rightSegWidthIn * SCALE_IN_TO_UNIT,
        WALL_HEIGHT_IN * SCALE_IN_TO_UNIT,
        WALL_THICKNESS_IN * SCALE_IN_TO_UNIT,
      ),
    [rightSegWidthIn],
  );

  // Top wall segment: above the door, full width
  const topSegHeightIn = WALL_HEIGHT_IN - DOOR_HEIGHT_IN;
  const topSegGeom = useMemo(
    () =>
      new BoxGeometry(
        wallWidthIn * SCALE_IN_TO_UNIT,
        topSegHeightIn * SCALE_IN_TO_UNIT,
        WALL_THICKNESS_IN * SCALE_IN_TO_UNIT,
      ),
    [wallWidthIn, topSegHeightIn],
  );

  const wallMaterial = useMemo(
    () =>
      new MeshStandardMaterial({
        color: 0x8a9099,
        roughness: 0.9,
        metalness: 0.02,
      }),
    [],
  );

  const doorFrameMaterial = useMemo(
    () =>
      new MeshStandardMaterial({
        color: 0x6a6a6a,
        roughness: 0.85,
        metalness: 0.02,
      }),
    [],
  );

  // Back edge of the head platform (in Z)
  const platformBackEdgeZ = platformZ - (platformDepthIn / 2) * SCALE_IN_TO_UNIT;

  // Wall sits flush against the back of the platform, extending downward from above
  const wallZ = platformBackEdgeZ - (WALL_THICKNESS_IN / 2) * SCALE_IN_TO_UNIT;

  // X centers of left and right segments
  const leftSegCenterX = (-(DOOR_WIDTH_IN / 2) - leftSegWidthIn / 2) * SCALE_IN_TO_UNIT;
  const rightSegCenterX = (DOOR_WIDTH_IN / 2 + rightSegWidthIn / 2) * SCALE_IN_TO_UNIT;

  // Y centers
  const wallCenterY = (WALL_HEIGHT_IN / 2) * SCALE_IN_TO_UNIT;
  const topSegCenterY = (DOOR_HEIGHT_IN + topSegHeightIn / 2) * SCALE_IN_TO_UNIT;

  return (
    <>
      {/* Left wall segment */}
      <mesh
        geometry={leftSegGeom}
        material={wallMaterial}
        position={[leftSegCenterX, wallCenterY, wallZ]}
        receiveShadow
        castShadow
        userData={{ kind: 'building-context', refId: 'building-wall-left' }}
      />
      {/* Right wall segment */}
      <mesh
        geometry={rightSegGeom}
        material={wallMaterial}
        position={[rightSegCenterX, wallCenterY, wallZ]}
        receiveShadow
        castShadow
        userData={{ kind: 'building-context', refId: 'building-wall-right' }}
      />
      {/* Top wall segment (above door) */}
      <mesh
        geometry={topSegGeom}
        material={wallMaterial}
        position={[0, topSegCenterY, wallZ]}
        receiveShadow
        castShadow
        userData={{ kind: 'building-context', refId: 'building-wall-top' }}
      />
      {/* Door frame — thin darker strips around the opening (left jamb) */}
      <mesh
        material={doorFrameMaterial}
        position={[
          (-DOOR_WIDTH_IN / 2) * SCALE_IN_TO_UNIT,
          (DOOR_HEIGHT_IN / 2) * SCALE_IN_TO_UNIT,
          wallZ + (WALL_THICKNESS_IN / 2 + 0.5) * SCALE_IN_TO_UNIT,
        ]}
        receiveShadow
        userData={{ kind: 'building-context', refId: 'door-frame-left' }}
      >
        <boxGeometry
          args={[2 * SCALE_IN_TO_UNIT, DOOR_HEIGHT_IN * SCALE_IN_TO_UNIT, 1 * SCALE_IN_TO_UNIT]}
        />
      </mesh>
      {/* Door frame — right jamb */}
      <mesh
        material={doorFrameMaterial}
        position={[
          (DOOR_WIDTH_IN / 2) * SCALE_IN_TO_UNIT,
          (DOOR_HEIGHT_IN / 2) * SCALE_IN_TO_UNIT,
          wallZ + (WALL_THICKNESS_IN / 2 + 0.5) * SCALE_IN_TO_UNIT,
        ]}
        receiveShadow
        userData={{ kind: 'building-context', refId: 'door-frame-right' }}
      >
        <boxGeometry
          args={[2 * SCALE_IN_TO_UNIT, DOOR_HEIGHT_IN * SCALE_IN_TO_UNIT, 1 * SCALE_IN_TO_UNIT]}
        />
      </mesh>
      {/* Door frame — top header */}
      <mesh
        material={doorFrameMaterial}
        position={[
          0,
          DOOR_HEIGHT_IN * SCALE_IN_TO_UNIT,
          wallZ + (WALL_THICKNESS_IN / 2 + 0.5) * SCALE_IN_TO_UNIT,
        ]}
        receiveShadow
        userData={{ kind: 'building-context', refId: 'door-frame-top' }}
      >
        <boxGeometry
          args={[
            (DOOR_WIDTH_IN + 4) * SCALE_IN_TO_UNIT,
            2 * SCALE_IN_TO_UNIT,
            1 * SCALE_IN_TO_UNIT,
          ]}
        />
      </mesh>
    </>
  );
}
